/**
 * Sticker Print Modal - Generate print-ready QR code stickers
 * With live design preview: custom colors, dot shapes, logo overlay
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Printer, Download, Grid, FileText, Loader2,
  Palette, Image as ImageIcon, Square, Circle, Diamond, Upload, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SHAPE_OPTIONS = [
  { value: 'square', label: 'Square', icon: Square },
  { value: 'rounded', label: 'Rounded', icon: Square },
  { value: 'dots', label: 'Dots', icon: Circle },
  { value: 'diamond', label: 'Diamond', icon: Diamond },
];

const COLOR_PRESETS = [
  { fg: '#000000', bg: '#FFFFFF', name: 'Classic' },
  { fg: '#1a1a2e', bg: '#e8e8f0', name: 'Midnight' },
  { fg: '#0f4c75', bg: '#bbe1fa', name: 'Ocean' },
  { fg: '#2d6a4f', bg: '#d8f3dc', name: 'Forest' },
  { fg: '#6a040f', bg: '#fdf0d5', name: 'Ruby' },
  { fg: '#5a189a', bg: '#e0aaff', name: 'Grape' },
  { fg: '#e36414', bg: '#fefae0', name: 'Amber' },
  { fg: '#023047', bg: '#8ecae6', name: 'Teal' },
];

const StickerPrintModal = ({ isOpen, onClose, selectedTags = [], batchId = null }) => {
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState('a4');
  const [stickerSize, setStickerSize] = useState('2inch');
  const [includeLabels, setIncludeLabels] = useState(true);
  const [preview, setPreview] = useState(null);

  // Design state
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [dotShape, setDotShape] = useState('square');
  const [logoB64, setLogoB64] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('layout'); // layout | design
  const fileInputRef = useRef(null);

  // Load layout preview
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/stickers/preview`, {
          params: { page_size: pageSize, sticker_size: stickerSize, include_labels: includeLabels }
        });
        setPreview(res.data);
      } catch (e) {
        console.error('Preview error:', e);
      }
    };
    if (isOpen) loadPreview();
  }, [isOpen, pageSize, stickerSize, includeLabels]);

  // Debounced live QR preview
  const loadQrPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/stickers/qr-preview`,
        { text: 'https://app.scanit2go.com/demo', fg_color: fgColor, bg_color: bgColor, dot_shape: dotShape, logo_b64: logoB64 },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      setQrPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      console.error('QR preview error:', e);
    } finally {
      setPreviewLoading(false);
    }
  }, [fgColor, bgColor, dotShape, logoB64]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(loadQrPreview, 350);
    return () => clearTimeout(timer);
  }, [isOpen, fgColor, bgColor, dotShape, logoB64, loadQrPreview]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => { if (qrPreviewUrl) URL.revokeObjectURL(qrPreviewUrl); };
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) {
      toast.error('Logo must be under 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(',')[1];
      setLogoB64(b64);
      setLogoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoB64(null);
    setLogoName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    const codes = selectedTags.map(t => t.nfc_code || t);
    if (codes.length === 0 && !batchId) {
      toast.error('No tags selected');
      return;
    }
    setLoading(true);
    try {
      let response;
      const styleParams = { fg_color: fgColor, bg_color: bgColor, dot_shape: dotShape, logo_b64: logoB64 };
      if (batchId) {
        response = await axios.post(
          `${BACKEND_URL}/api/admin/batch/${batchId}/stickers`, null,
          { params: { page_size: pageSize, sticker_size: stickerSize, include_labels: includeLabels, base_url: window.location.origin }, responseType: 'blob' }
        );
      } else {
        response = await axios.post(
          `${BACKEND_URL}/api/admin/stickers/generate`,
          { nfc_codes: codes, page_size: pageSize, sticker_size: stickerSize, include_labels: includeLabels, base_url: window.location.origin, ...styleParams },
          { responseType: 'blob' }
        );
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-stickers-${codes.length || 'batch'}pcs.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Sticker PDF generated! Check your downloads.');
      onClose();
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate stickers');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tagCount = selectedTags.length || (batchId ? 'batch' : 0);
  const totalPages = preview ? Math.ceil(tagCount / preview.stickers_per_page) : 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="sticker-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-border max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="sticker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Printer className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold">Print QR Stickers</h2>
                <p className="text-xs text-muted-foreground">
                  {typeof tagCount === 'number' ? `${tagCount} stickers` : 'Batch stickers'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors" data-testid="sticker-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex border-b border-border shrink-0">
            <button
              onClick={() => setActiveSection('layout')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeSection === 'layout'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="sticker-tab-layout"
            >
              <Grid className="w-4 h-4" /> Layout
            </button>
            <button
              onClick={() => setActiveSection('design')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeSection === 'design'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="sticker-tab-design"
            >
              <Palette className="w-4 h-4" /> Design
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 p-4">
            {activeSection === 'layout' && (
              <div className="space-y-4" data-testid="sticker-layout-section">
                {/* Page Size */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Page Size</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'a4', label: 'A4 (210×297mm)' },
                      { value: 'letter', label: 'Letter (8.5×11")' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setPageSize(opt.value)}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all ${
                          pageSize === opt.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`page-size-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sticker Size */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Sticker Size</label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { value: '1inch', label: '1"' },
                      { value: '1.5inch', label: '1.5"' },
                      { value: '2inch', label: '2"' },
                      { value: '2.5inch', label: '2.5"' },
                      { value: '3inch', label: '3"' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setStickerSize(opt.value)}
                        className={`py-2 rounded-lg border text-sm font-medium transition-all ${
                          stickerSize === opt.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`sticker-size-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLabels}
                    onChange={e => setIncludeLabels(e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                    data-testid="include-labels-checkbox"
                  />
                  <span className="text-sm">Include tag labels</span>
                </label>

                {/* Layout preview */}
                {preview && (
                  <div className="bg-secondary/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Grid className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Layout Preview</span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-primary">{preview.columns}×{preview.rows}</div>
                        <div className="text-xs text-muted-foreground">Grid</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{preview.stickers_per_page}</div>
                        <div className="text-xs text-muted-foreground">Per page</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{typeof tagCount === 'number' ? totalPages : '?'}</div>
                        <div className="text-xs text-muted-foreground">Total pages</div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <div
                        className="border border-border rounded bg-white p-2"
                        style={{ width: 120, height: pageSize === 'a4' ? 170 : 155 }}
                      >
                        <div
                          className="grid gap-0.5"
                          style={{
                            gridTemplateColumns: `repeat(${preview.columns}, 1fr)`,
                            gridTemplateRows: `repeat(${preview.rows}, 1fr)`,
                            height: '100%'
                          }}
                        >
                          {Array(Math.min(preview.stickers_per_page, 30)).fill(0).map((_, i) => (
                            <div key={i} className="bg-gray-200 rounded-sm flex items-center justify-center">
                              <div className="w-2/3 h-2/3 rounded-sm" style={{ backgroundColor: fgColor + '40' }}></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeSection === 'design' && (
              <div className="space-y-5" data-testid="sticker-design-section">
                {/* Live QR Preview */}
                <div className="flex justify-center">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border" style={{ backgroundColor: bgColor }}>
                    {qrPreviewUrl ? (
                      <img
                        src={qrPreviewUrl}
                        alt="QR Preview"
                        className="w-48 h-48 object-contain"
                        data-testid="qr-live-preview"
                      />
                    ) : (
                      <div className="w-48 h-48 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {previewLoading && (
                      <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Color Presets */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Color Presets</label>
                  <div className="grid grid-cols-4 gap-2">
                    {COLOR_PRESETS.map(p => (
                      <button
                        key={p.name}
                        onClick={() => { setFgColor(p.fg); setBgColor(p.bg); }}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-xs font-medium transition-all ${
                          fgColor === p.fg && bgColor === p.bg
                            ? 'border-primary ring-1 ring-primary/30'
                            : 'border-border hover:border-primary/40'
                        }`}
                        data-testid={`color-preset-${p.name.toLowerCase()}`}
                      >
                        <div className="flex shrink-0">
                          <div className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: p.fg }}></div>
                          <div className="w-4 h-4 rounded-full border border-border -ml-1.5" style={{ backgroundColor: p.bg }}></div>
                        </div>
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">QR Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={fgColor}
                        onChange={e => setFgColor(e.target.value)}
                        className="w-10 h-10 rounded-lg border border-border cursor-pointer"
                        data-testid="fg-color-picker"
                      />
                      <input
                        type="text"
                        value={fgColor}
                        onChange={e => setFgColor(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
                        data-testid="fg-color-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Background</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={bgColor}
                        onChange={e => setBgColor(e.target.value)}
                        className="w-10 h-10 rounded-lg border border-border cursor-pointer"
                        data-testid="bg-color-picker"
                      />
                      <input
                        type="text"
                        value={bgColor}
                        onChange={e => setBgColor(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
                        data-testid="bg-color-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Dot Shape */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Dot Shape</label>
                  <div className="grid grid-cols-4 gap-2">
                    {SHAPE_OPTIONS.map(s => {
                      const Icon = s.icon;
                      return (
                        <button
                          key={s.value}
                          onClick={() => setDotShape(s.value)}
                          className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                            dotShape === s.value
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`dot-shape-${s.value}`}
                        >
                          <Icon className="w-4 h-4" />
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Center Logo (optional)</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  {logoB64 ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/30">
                      <img
                        src={`data:image/png;base64,${logoB64}`}
                        alt="Logo"
                        className="w-10 h-10 rounded object-contain bg-white border border-border"
                        data-testid="logo-preview-thumb"
                      />
                      <span className="text-sm truncate flex-1">{logoName}</span>
                      <button
                        onClick={removeLogo}
                        className="p-1.5 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-3 rounded-lg border-2 border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-foreground transition-all flex items-center justify-center gap-2 text-sm"
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4" />
                      Upload logo (PNG, JPG — max 500KB)
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>PDF download</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                data-testid="sticker-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={loading || (typeof tagCount === 'number' && tagCount === 0)}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50 font-medium"
                data-testid="generate-pdf-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StickerPrintModal;
